import { Injectable } from '@angular/core';
import { FiBackendService } from '../../shared/fi-backend/fi-backend.service';
import { IPKImportLog, IPKRoleMatch, IPKSettings, IPKUnitExternal, IPKUnitMatch, IPKUserMatch } from './pk';
import { LoadingService } from '../../shared/fi-loading/loading.service';
import { UserModel } from '../../user/user.model';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '../../shared/fi-items-list/fi-items-list';
import { IPKCustomerData } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { ILightWeightBusinessUnit } from '@fi-shared/models';
import { FiImageSize } from '@fi-shared/types';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

@Injectable()
export class PkService {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoadingService: LoadingService,
        private fiPrintDownloadService: FiPrintDownloadService,
    ) {}

    getAvailableUsersForMatch() {
        return this.fiBackend.fetchData<UserModel>('PK/PK/GetAvailableUsersForMatching');
    }

    getPKSettings() {
        return this.fiBackend.fetchData<IPKSettings>('PK/PK/GetPKSettings');
    }

    savePKSettings(pkSettings: IPKSettings) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<IPKCustomerData>('PK/PK/SavePKSettings', pkSettings),
        );
    }

    getAvailableBusinessUnits() {
        const promise = this.fiBackend.fetchData<ILightWeightBusinessUnit[]>('PK/PK/GetAvailableBusinessUnits');
        return promise;
    }

    getExistingUnitMatches() {
        const promise = this.fiBackend.fetchData<IPKUnitMatch[]>('PK/PK/GetExistingUnitMatches');
        return promise;
    }

    saveUnitMatches(unitMatches: IPKUnitMatch[]) {
        const promise = this.fiBackend.fetchDataPost<void>('PK/PK/SaveUnitMatches', unitMatches);
        return this.fiLoadingService.trackPromise(promise);
    }

    getUnitMatchesFromFile(files: FileList) {
        const promise = new Promise<IPKUnitExternal[]>((resolve, reject) => {
            try {
                const fileReader = new FileReader();
                fileReader.readAsText(files[0]);
                fileReader.onload = (result) => {
                    try {
                        const data: IPKUnitExternal[] = JSON.parse(result.target.result as string);
                        resolve(data);
                    } catch (e) {
                        reject();
                    }
                };
            } catch (e) {
                reject();
            }
        });
        return this.fiLoadingService.trackPromise(promise);
    }

    getRoleMatches() {
        const promise = this.fiBackend.fetchData<IPKRoleMatch[]>('PK/PK/GetRoleMatches');
        return this.fiLoadingService.trackPromise(promise);
    }

    getExistingRoleMatches() {
        return this.fiBackend.fetchData<IPKRoleMatch[]>('PK/PK/GetExistingRoleMatches');
    }

    saveRoleMatches(roleMatches: IPKRoleMatch[]) {
        const promise = this.fiBackend.fetchDataPost<void>('PK/PK/SaveRoleMatches', roleMatches);
        return this.fiLoadingService.trackPromise(promise);
    }

    getUserMatches() {
        return this.fiBackend.fetchData<IPKUserMatch[]>('PK/PK/GetUserMatches');
    }

    saveUserMatches(userMatches: IPKUserMatch[]) {
        return this.fiBackend.fetchDataPost<void>('PK/PK/SaveUserMatches', userMatches);
    }

    isAutoImportAvailable() {
        return this.fiBackend.fetchData<IPKSettings>('PK/PK/IsAutoImportAvailable');
    }

    testImportUsers() {
        return this.fiBackend.fetchDataPost<IPKImportLog>('PK/PK/TestImportUsers');
    }

    importUsers() {
        return this.fiBackend.fetchDataPost<IPKImportLog>('PK/PK/ImportUsers');
    }

    getImportLogListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IPKImportLog>>(
            'PK/PK/GetImportLogListData',
            parameter,
        );
    }

    getImportLog(importLogId: number) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchData<IPKImportLog>(`PK/PK/GetImportLog/${importLogId}`),
        );
    }

    uploadUsersJsonForTest(file: File) {
        const fd = new FormData();
        fd.append('File', file);
        return this.fiBackend.fetchDataPost('PK/PK/UploadUsersJsonForTest', fd);
    }

    getApiResponse() {
        const promise = this.fiPrintDownloadService.downloadBinaryFile('PK/PK/GetApiResponse', {
            fileName: 'pk_api_response.json',
            size: FiImageSize.Original,
        });
        return promise;
    }
}
